export type CurrencyInputScale =
  | "auto"
  | "raw"
  | "thousands"
  | "millions"
  | "billions";

export type FormatCompactCurrencyOptions = {
  /** Returned when input is null, undefined, empty, or NaN. Default "NA". */
  fallback?: string;
  /** Returned when numeric input is exactly 0. Default "$0". */
  zeroValue?: string;
  /** When true, non-numeric strings are returned unchanged. Default true. */
  preserveFormattedStrings?: boolean;
  /**
   * How to interpret numeric input before compact formatting.
   * Use "millions" when the value is already in millions (345 → $345M).
   * "auto"/"raw" treat the value as dollars unless it looks pre-scaled.
   */
  inputScale?: CurrencyInputScale;
};

type CompactSuffix = "k" | "M" | "B" | "T";

const COMPACT_SUFFIX_PATTERN = /[kmbt]$/i;
const COMPACT_CURRENCY_STRING = /^\$?(-?\d+(?:\.\d+)?)([kmbt])?\+?$/i;
const MILLIONS_SCALE_METRIC_PATTERN =
  /\b(arr|mrr|annual[_\s-]?revenue|annual[_\s-]?recurring|monthly[_\s-]?recurring)\b/i;

const SCALE_MULTIPLIERS: Record<
  Exclude<CurrencyInputScale, "auto" | "raw">,
  { threshold: number; multiplier: number }
> = {
  thousands: { threshold: 1_000, multiplier: 1_000 },
  millions: { threshold: 1_000_000, multiplier: 1_000_000 },
  billions: { threshold: 1_000_000_000, multiplier: 1_000_000_000 },
};

const normalizeSuffix = (suffix: string): CompactSuffix => {
  const lower = suffix.toLowerCase();
  if (lower === "k") return "k";
  if (lower === "m") return "M";
  if (lower === "b") return "B";
  return "T";
};

const formatWithSuffix = (amount: number, suffix: CompactSuffix): string => {
  const sign = amount < 0 ? "-" : "";
  const absAmount = Math.abs(amount);
  const formatted =
    absAmount >= 10 || suffix === "k"
      ? String(Math.round(absAmount))
      : String(Math.round(absAmount * 10) / 10);
  return `${sign}$${formatted}${suffix}`;
};

const formatAutoScaled = (value: number): string => {
  const abs = Math.abs(value);

  if (abs >= 1_000_000_000_000) {
    return formatWithSuffix(value / 1_000_000_000_000, "T");
  }
  if (abs >= 1_000_000_000) {
    return formatWithSuffix(value / 1_000_000_000, "B");
  }
  if (abs >= 1_000_000) {
    return formatWithSuffix(value / 1_000_000, "M");
  }
  if (abs >= 1_000) {
    return formatWithSuffix(value / 1_000, "k");
  }

  const sign = value < 0 ? "-" : "";
  return `${sign}$${Math.round(value)}`;
};

const toRawDollars = (
  value: number,
  inputScale: CurrencyInputScale
): number => {
  if (inputScale === "auto" || inputScale === "raw") {
    return value;
  }

  const { threshold, multiplier } = SCALE_MULTIPLIERS[inputScale];
  return Math.abs(value) >= threshold ? value : value * multiplier;
};

const normalizeCompactCurrencyString = (value: string): string | null => {
  const normalized = value.replace(/\s+/g, "").replace(/\+$/, "");
  const match = normalized.match(COMPACT_CURRENCY_STRING);
  if (!match) return null;

  const amount = Number.parseFloat(match[1]);
  if (!Number.isFinite(amount)) return null;

  if (match[2]) {
    return formatWithSuffix(amount, normalizeSuffix(match[2]));
  }

  return formatAutoScaled(amount);
};

/** Infer whether a metric value is already expressed in millions/thousands/billions. */
export const resolveCurrencyInputScale = (
  scale?: string,
  unitOfMeasure?: string,
  metricName?: string,
  metricId?: string
): CurrencyInputScale => {
  const scaleLower = scale?.toLowerCase().trim() || "";
  const uomLower = unitOfMeasure?.toLowerCase().trim() || "";
  const nameLower = metricName?.toLowerCase().trim() || "";
  const idLower = metricId?.toLowerCase().trim() || "";

  if (
    scaleLower === "millions" ||
    scaleLower === "million" ||
    uomLower === "m" ||
    uomLower.includes("million")
  ) {
    return "millions";
  }
  if (
    scaleLower === "billions" ||
    scaleLower === "billion" ||
    uomLower === "b" ||
    uomLower.includes("billion")
  ) {
    return "billions";
  }
  if (
    scaleLower === "thousands" ||
    scaleLower === "thousand" ||
    uomLower === "k" ||
    uomLower.includes("thousand")
  ) {
    return "thousands";
  }

  if (
    MILLIONS_SCALE_METRIC_PATTERN.test(nameLower) ||
    MILLIONS_SCALE_METRIC_PATTERN.test(idLower)
  ) {
    return "millions";
  }

  return "raw";
};

/**
 * Format numeric amounts as compact currency: $10k, $1M, $1.2B, $2T.
 */
export function formatCompactCurrency(
  value: number | string | null | undefined,
  options: FormatCompactCurrencyOptions = {}
): string {
  const {
    fallback = "NA",
    zeroValue = "$0",
    preserveFormattedStrings = true,
    inputScale = "raw",
  } = options;

  if (value === null || value === undefined) {
    return fallback;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed || trimmed.toLowerCase() === "n/a") {
      return fallback;
    }

    const normalizedCompact = normalizeCompactCurrencyString(trimmed);
    if (normalizedCompact) {
      return normalizedCompact === "$0" ? zeroValue : normalizedCompact;
    }

    if (preserveFormattedStrings) {
      const normalized = trimmed.replace(/\s+/g, "");
      if (
        normalized.startsWith("$") &&
        COMPACT_SUFFIX_PATTERN.test(normalized)
      ) {
        const remapped = normalizeCompactCurrencyString(normalized);
        if (remapped) return remapped;
      }
    }

    const parsed = Number.parseFloat(trimmed.replace(/[^0-9.-]/g, ""));
    if (Number.isNaN(parsed)) {
      return preserveFormattedStrings ? trimmed : fallback;
    }

    return formatCompactCurrency(parsed, {
      ...options,
      preserveFormattedStrings: false,
    });
  }

  if (Number.isNaN(value)) {
    return fallback;
  }

  if (value === 0) {
    return zeroValue;
  }

  const rawValue = toRawDollars(value, inputScale);
  return formatAutoScaled(rawValue);
}

/** Format currency metrics with optional scale / unit / name hints from Hawkeye KPI data. */
export function formatCurrencyMetricValue(
  value: number,
  options: {
    scale?: string;
    unitOfMeasure?: string;
    metricName?: string;
    metricId?: string;
    fallback?: string;
  } = {}
): string {
  if (!Number.isFinite(value)) {
    return options.fallback ?? "NA";
  }

  const inputScale = resolveCurrencyInputScale(
    options.scale,
    options.unitOfMeasure,
    options.metricName,
    options.metricId
  );

  return formatCompactCurrency(value, {
    fallback: options.fallback ?? "NA",
    inputScale,
  });
}

/** Alias used by Account Overview / KPI cards. */
export const formatCompactCurrencyDisplay = formatCompactCurrency;
